import { useState } from 'react';
import { ClipboardList, Eye, Share2, Star, Users } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import { participationBand } from '@/features/teacher/dailyReport/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatDate, formatDateTime } from '@/lib/format';
import type { SchoolDailyReport } from '@/types/dailyReport';
import { schoolDailyReportsApi } from '../api/dailyReports.api';
import { SchoolDailyReportModal } from '../components/SchoolDailyReportModal';

const PAGE_SIZE = 20;

/**
 * The screen's filters, as they sit in the address bar. An empty `date` means
 * today, which the server decides in IST — not this laptop. The teacher and
 * class are uuids the server scopes, so there is no fixed set to check them
 * against.
 */
const DEFAULT_FILTERS = { date: '', teacher: '', section: '', page: 1 };

const columns = (onView: (row: SchoolDailyReport) => void): Column<SchoolDailyReport>[] => [
  {
    key: 'class',
    header: 'Class',
    render: (row) => (
      <div className="min-w-0">
        <p className="whitespace-nowrap font-[560] text-ink">{row.displayName}</p>
        <p className="text-xs text-ink-3">{row.subjectName}</p>
      </div>
    ),
  },
  {
    key: 'teacher',
    header: 'Teacher',
    render: (row) => <span className="text-ink-2">{row.savedBy}</span>,
  },
  {
    key: 'topic',
    header: 'Topic covered',
    hideOnMobile: true,
    render: (row) => <p className="line-clamp-2 max-w-md break-words text-ink-2">{row.content}</p>,
  },
  {
    key: 'participation',
    header: 'Participation',
    hideOnMobile: true,
    render: (row) => {
      const band = participationBand(row.participationPercent);
      return band ? <Badge tone={band.tone}>{band.label}</Badge> : <span className="text-ink-3">—</span>;
    },
  },
  {
    key: 'saved',
    header: 'Saved',
    hideOnMobile: true,
    render: (row) => <span className="whitespace-nowrap text-ink-2">{formatDateTime(row.savedAt)}</span>,
  },
  {
    key: 'actions',
    header: <span className="sr-only">Actions</span>,
    align: 'right',
    render: (row) => (
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Eye className="h-4 w-4" aria-hidden />}
        aria-label={`View the report for ${row.displayName}, ${row.subjectName}`}
        onClick={() => onView(row)}
      >
        View
      </Button>
    ),
  },
];

/**
 * What every teacher wrote about their classes, one day at a time.
 *
 * Read only: a report stays the words of the teacher who wrote it. The server
 * gates this on `DAILY_REPORT_REVIEW`, so a role without the key is refused and
 * sees the error state rather than an empty day.
 */
export function SchoolDailyReportsPage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const { date, teacher, section, page } = filters;
  const [viewing, setViewing] = useState<SchoolDailyReport | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      schoolDailyReportsApi.day({
        onDate: date || undefined,
        teacherUuid: teacher || undefined,
        classSectionUuid: section || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    [date, teacher, section, page],
  );

  const isFiltered = Boolean(teacher || section);
  const summary = data?.summary;
  const band = participationBand(summary?.participation ?? null);

  function clearFilters() {
    setFilters({ teacher: '', section: '', page: 1 });
  }

  return (
    <>
      <PageHeader
        title="Daily Report"
        description="What each teacher wrote about their classes, one day at a time."
      />

      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,1fr))_auto]">
        {/* The choices below are that day's, so a new day starts from all of them. */}
        <DatePicker
          label="Date"
          value={date || data?.date || ''}
          onChange={(event) =>
            setFilters({ date: event.target.value, teacher: '', section: '', page: 1 })
          }
        />
        <Select
          label="Teacher"
          options={[
            { value: '', label: 'Every teacher' },
            ...(data?.filters.teachers ?? []).map((option) => ({
              value: option.uuid,
              label: option.name,
            })),
          ]}
          value={teacher}
          onChange={(event) => setFilters({ teacher: event.target.value, page: 1 })}
        />
        <Select
          label="Class"
          options={[
            { value: '', label: 'Every class' },
            ...(data?.filters.classSections ?? []).map((option) => ({
              value: option.uuid,
              label: option.displayName,
            })),
          ]}
          value={section}
          onChange={(event) => setFilters({ section: event.target.value, page: 1 })}
        />
        <ClearFiltersButton isActive={isFiltered} onClear={clearFilters} />
      </div>

      {error ? (
        <QueryError title="Could not load the daily reports" error={error} onRetry={refetch} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricTile
              label="Reports written"
              value={summary?.reports ?? 0}
              icon={<ClipboardList className="h-5 w-5" aria-hidden />}
              tone="indigo"
              isLoading={isLoading}
            />
            <MetricTile
              label="Teachers reporting"
              value={summary?.teachers ?? 0}
              icon={<Users className="h-5 w-5" aria-hidden />}
              tone="blue"
              isLoading={isLoading}
            />
            <MetricTile
              label="Average participation"
              value={band?.label ?? '—'}
              icon={<Star className="h-5 w-5" aria-hidden />}
              tone="amber"
              isLoading={isLoading}
            />
            <MetricTile
              label="Shared with parents"
              value={summary?.sharedWithParents ?? 0}
              icon={<Share2 className="h-5 w-5" aria-hidden />}
              tone="emerald"
              isLoading={isLoading}
            />
          </div>

          <div className="card overflow-hidden">
            <Table
              variant="plain"
              columns={columns(setViewing)}
              rows={data?.items ?? []}
              rowKey={(row) => row.uuid}
              isLoading={isLoading}
              empty={
                <EmptyState
                  icon={<ClipboardList className="h-5 w-5" />}
                  title={isFiltered ? 'No reports match these filters' : 'No reports for this day'}
                  description={
                    isFiltered
                      ? 'Change or clear the filters to see the rest.'
                      : data
                        ? `No teacher has written up a class for ${formatDate(data.date)}.`
                        : undefined
                  }
                  action={
                    isFiltered ? (
                      <Button variant="secondary" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
            {data && (
              <Pagination
                meta={data.meta}
                onPageChange={(next) => setFilters({ page: next })}
                itemLabel="reports"
              />
            )}
          </div>
        </>
      )}

      <SchoolDailyReportModal report={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
